import { z } from 'zod';

/*
 * Convenção (docs/04-contrato-api.md, decisão de 13/10): os campos JSON são os nomes literais das tabelas
 * Progress ("fm-codigo", "nr-plano", "dt-hr-inicio"…), em português e com hífen, sem camada de tradução.
 */

/** Data pura: "2026-10-12" */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD');
/** Data e hora com offset: "2026-10-12T14:00:00-03:00" */
export const isoDateTimeSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?([+-]\d{2}:\d{2}|Z)$/,
    'Data e hora devem estar em ISO 8601 com offset',
  );
/** Token de versão opaco (string derivada de dt-hr-alteracao); devolver igual nas escritas */
export const versionSchema = z.string();

export type ISODate = z.infer<typeof isoDateSchema>;
export type ISODateTime = z.infer<typeof isoDateTimeSchema>;
export type Version = z.infer<typeof versionSchema>;

/**
 * Campo opcional que aceita `null` vindo do ABL e o normaliza para `undefined`,
 * para que o tipo inferido seja `T | undefined` como no contrato.
 */
export const optional = <T extends z.ZodTypeAny>(schema: T) =>
  schema
    .nullish()
    .transform((v): z.output<T> | undefined => (v === null ? undefined : (v as z.output<T> | undefined)));

export const grupoSchema = z.enum(['CAL', 'LAM', 'COA', 'SLT']);
/** 1 Planejado · 2 Em produção · 3 Em cura · 4 Concluído · 5 Cancelado */
export const situacaoProgSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export const statusPedidoSchema = z.enum(['no-prazo', 'em-risco', 'atrasado', 'nao-programado']);
/** 1 Rascunho · 2 Publicado · 3 Substituído */
export const situacaoPlanoSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

export type Grupo = z.infer<typeof grupoSchema>;
export type SituacaoProg = z.infer<typeof situacaoProgSchema>;
export type StatusPedido = z.infer<typeof statusPedidoSchema>;
export type SituacaoPlano = z.infer<typeof situacaoPlanoSchema>;

export const GRUPOS: readonly Grupo[] = grupoSchema.options;

export const SITUACAO_PLANO = { RASCUNHO: 1, PUBLICADO: 2, SUBSTITUIDO: 3 } as const;
export const SITUACAO_PROG = {
  PLANEJADO: 1,
  EM_PRODUCAO: 2,
  EM_CURA: 3,
  CONCLUIDO: 4,
  CANCELADO: 5,
} as const;

export const paginaSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
  });
export interface Pagina<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const errorCodeSchema = z.enum([
  'VALIDATION',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VERSION_CONFLICT',
  'BUSINESS_RULE',
  'RATE_LIMITED',
  'DATASUL_UNAVAILABLE',
  'DATASUL_CONTRACT',
  'TWO_FACTOR_SETUP_REQUIRED',
  'PASSWORD_CHANGE_REQUIRED',
  'INTERNAL',
]);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const apiErroSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});
export type ApiErro = z.infer<typeof apiErroSchema>;

/** Corpo de `recalcular` e `publicar`. */
export const versionBodySchema = z.object({ version: versionSchema });
export type VersionBody = z.infer<typeof versionBodySchema>;

/**
 * Linha do `tt-retorno` das escritas em lote (padrão dos `reference/*.p`): cada linha da requisição
 * volta com o seu próprio resultado, na mesma ordem. Não existe status HTTP por linha, só no envelope.
 * Conflito de versão = `errornumber` 409.
 */
export const retornoLinhaSchema = z.object({
  situacao: z.enum(['OK', 'ERRO']),
  errornumber: z.number().int(),
  errordescription: z.string(),
});
export type RetornoLinha = z.infer<typeof retornoLinhaSchema>;

export const ERRO_CONFLITO_VERSAO = 409;
